import { type MouseEvent, type SyntheticEvent, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  Button,
  IconCube,
  IconCurrencyCircleDollar,
  IconEye,
  IconEyeSlash,
  Modal,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Tooltip,
  useModalState,
  useToast,
} from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import { IconInfo } from '@/components/Icon/IconInfo'
import { useProductSKUsOffersModal } from '@/components/products-listing/ProductSKUsOffersModal'
import { useAnalytics } from '@/contexts/analytics'
import { type ClickOnTheListEvent } from '@/contexts/analytics/customEvents'
import { useUpdateSku } from '@/hooks/useSKU'
import { executeClickHandlers } from '@/lib/middleware'
import { openAdminInTab } from '@/lib/navigate'
import { getProductStatus, PRODUCT_STATUS } from '@/lib/productStatus'
import type { Product, SKUListing } from '@/types/product'

import { CopySKUCode } from './CopySKUCode/CopySKUCode'

import * as style from './SKUQuickActions.styles'

interface SkuQuickActionsProps {
  product: Product
  sku: SKUListing
}

const defineMessages = (sku: SKUListing) =>
  ({
    openInventoryLabel: 'admin/admin.catalog.edit-inventory-menu-label',
    openPricesLabel: 'admin/admin.catalog.edit-prices-menu-label',
    openPriceAndInventory: 'admin/admin.catalog.price-and-inventory-tab-label',
    activateSku: {
      openModalActionLabel: sku.isActive
        ? 'admin/admin.catalog.inactivate-sku-label'
        : 'admin/admin.catalog.activate-sku-label',
      modalTitle: sku.isActive
        ? 'admin/admin.catalog.inactivate-sku-card-heading'
        : 'admin/admin.catalog.activate-sku-card-heading',
      modalContent: sku.isActive
        ? 'admin/admin.catalog.inactivate-sku-card-description'
        : 'admin/admin.catalog.activate-sku-card-description',
      cancelActionLabel:
        'admin/admin.catalog.activate-sku-card-cancel-action-label',
      confirmActionLabel: sku.isActive
        ? 'admin/admin.catalog.inactivate-sku-label'
        : 'admin/admin.catalog.activate-sku-label',
      apiSuccess: sku.isActive
        ? 'admin/admin.catalog.inactivate-sku-success-message'
        : 'admin/admin.catalog.activate-sku-success-message',
      apiError: sku.isActive
        ? 'admin/admin.catalog.inactivate-sku-error-message'
        : 'admin/admin.catalog.activate-sku-error-message',
    },
  }) as const

export function SKUQuickActions(props: SkuQuickActionsProps) {
  const { sku, product } = props

  const { formatMessage } = useIntl()

  const analytics = useAnalytics()

  const { account, workspace } = useAdmin()

  const activateSkuModalState = useModalState()

  const productSKUsOffersModal = useProductSKUsOffersModal()

  const [isSavingSku, setIsSavingSku] = useState(false)

  const showToast = useToast()

  const messages = defineMessages(sku)

  const preventEventPropagation = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation()
    evt.preventDefault()
  }

  const dispatchEventOnClick =
    (action: ClickOnTheListEvent['Action']) => () => {
      analytics.track('Clicked on the list', {
        Action: action,
      })
    }

  const updateSku = useUpdateSku({
    onSuccess: () => {
      setIsSavingSku(false)
      activateSkuModalState.hide()
      showToast({
        variant: 'positive',
        message: formatMessage({
          id: messages.activateSku.apiSuccess,
          defaultMessage: 'SKU activated successfully',
        }),
      })
    },
    onError: () => {
      setIsSavingSku(false)
      activateSkuModalState.hide()
      showToast({
        variant: 'critical',
        message: formatMessage({
          id: messages.activateSku.apiError,
          defaultMessage:
            "We couldn't activate the SKU. Please check the settings and try again.",
        }),
      })
    },
  })

  const handleActivateSku = () => {
    setIsSavingSku(true)
    updateSku(
      {
        IsActive: !sku.isActive,
        SkuId: String(sku.id),
        ActivateIfPossible: !sku.isActive,
      },
      String(product.id)
    )
  }

  const handleShowActivateSkuModal = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation()
    activateSkuModalState.show()
  }

  const handleShowSKUsOfferModal = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation()
    productSKUsOffersModal.show(product, sku.id)
  }

  const openPriceAndInventoryText = useMemo(() => {
    const skuStatus = getProductStatus(sku)

    if (skuStatus === PRODUCT_STATUS.pending) {
      return formatMessage({
        id: 'admin/admin.catalog.product-status.pending',
        defaultMessage: 'Pending product',
      })
    }

    if (skuStatus === PRODUCT_STATUS.inactive) {
      return formatMessage({
        id: 'admin/admin.catalog.product-status.inactive',
        defaultMessage: 'Inactive product',
      })
    }

    return formatMessage({
      id: messages.openPriceAndInventory,
      defaultMessage: 'Price and inventory',
    })
  }, [formatMessage, messages.openPriceAndInventory, sku])

  return (
    <>
      <Tooltip
        text={formatMessage({
          id: messages.openInventoryLabel,
          defaultMessage: 'Edit inventory',
        })}
        placement="top"
      >
        <Button
          className="sku-quick-action"
          variant="neutralTertiary"
          icon={<IconCube />}
          onClick={executeClickHandlers(
            preventEventPropagation,
            dispatchEventOnClick('Quick action: edit inventory'),
            openAdminInTab('inventory', '', account, workspace, {
              keyword: sku.id,
              skuId: sku.id,
            })
          )}
        />
      </Tooltip>
      <Tooltip
        text={formatMessage({
          id: messages.openPricesLabel,
          defaultMessage: 'Edit prices',
        })}
        placement="top"
      >
        <Button
          className="sku-quick-action"
          variant="neutralTertiary"
          icon={<IconCurrencyCircleDollar />}
          onClick={executeClickHandlers(
            preventEventPropagation,
            dispatchEventOnClick('Quick action: edit price'),
            openAdminInTab('pricing', '#/prices', account, workspace, {
              page: '1',
              qtd: '10',
              productId: product.id,
              productName: `${product.name} (SKU ${sku.id})`,
            })
          )}
        />
      </Tooltip>
      <Tooltip
        text={openPriceAndInventoryText}
        aria-label={openPriceAndInventoryText}
        placement="top"
      >
        <Button
          className="sku-quick-action"
          variant="neutralTertiary"
          icon={<IconInfo />}
          aria-label={formatMessage({
            id: messages.openPriceAndInventory,
            defaultMessage: 'Price and inventory',
          })}
          aria-labelledby={''}
          onClick={executeClickHandlers(
            preventEventPropagation,
            dispatchEventOnClick("Quick action: view offer's table"),
            handleShowSKUsOfferModal
          )}
        />
      </Tooltip>
      <CopySKUCode
        skuId={String(sku.id)}
        onClick={dispatchEventOnClick('Quick action: edit price')}
      />
      <>
        <Tooltip
          text={formatMessage({
            id: messages.activateSku.openModalActionLabel,
            defaultMessage: sku.isActive ? 'Deactivate' : 'Activate',
          })}
          placement="top"
        >
          <Button
            className="sku-quick-action"
            variant="neutralTertiary"
            icon={sku.isActive ? <IconEyeSlash /> : <IconEye />}
            onClick={executeClickHandlers(
              preventEventPropagation,
              dispatchEventOnClick('Quick action: activate/inactivate SKU'),
              handleShowActivateSkuModal
            )}
          />
        </Tooltip>
        <Modal
          state={activateSkuModalState}
          backdropProps={{ className: style.modalBackdrop }}
          onClick={(evt: SyntheticEvent<HTMLDivElement>) => {
            evt.stopPropagation()
          }}
          size="small"
        >
          <ModalHeader>
            <ModalTitle>
              {formatMessage({
                id: messages.activateSku.modalTitle,
                defaultMessage: sku.isActive
                  ? 'Deactivate SKU'
                  : 'Activate SKU',
              })}
            </ModalTitle>
            <ModalDismiss onClick={activateSkuModalState.hide} />
          </ModalHeader>
          <ModalContent>
            {formatMessage({
              id: messages.activateSku.modalContent,
              defaultMessage: sku.isActive
                ? 'Are you sure you want to deactivate this SKU?'
                : 'Are you sure you want to activate this SKU?',
            })}
          </ModalContent>
          <ModalFooter>
            <Button
              className={style.modalAction}
              size="large"
              variant="secondary"
              onClick={activateSkuModalState.hide}
              disabled={isSavingSku}
            >
              {formatMessage({
                id: messages.activateSku.cancelActionLabel,
                defaultMessage: 'Cancel',
              })}
            </Button>
            <Button
              className={style.modalAction}
              size="large"
              onClick={handleActivateSku}
              loading={isSavingSku}
            >
              {formatMessage({
                id: messages.activateSku.confirmActionLabel,
                defaultMessage: sku.isActive ? 'Deactivate' : 'Activate',
              })}
            </Button>
          </ModalFooter>
        </Modal>
      </>
    </>
  )
}
